import {
  Directive,
  ElementRef,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
} from '@angular/core';
import { Alignment, Fit, Layout, Rive, RiveParameters } from '@rive-app/canvas';

export type RiveAnimationConfig = Omit<RiveParameters, 'canvas' | 'src' | 'onLoad'>;

@Directive({
  selector: 'canvas[sbRiveAnimation]',
  exportAs: 'sb-rive-animation',
})
export class RiveAnimationDirective implements OnChanges, OnDestroy {
  private el = inject(ElementRef);

  @Input({ required: true, alias: 'sbRiveAnimation' })
  path!: string;

  loadCompleted = output<void>();

  loading = true;

  private riveAnimation?: Rive;

  sbRiveAnimationConfig = input<RiveAnimationConfig>({ autoplay: true });

  ngOnChanges(changes: SimpleChanges) {
    if (changes['path'] && this.path) {
      this.riveAnimation = new Rive({
        src: this.path,
        canvas: this.el.nativeElement,
        autoplay: true,
        layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
        onLoad: () => {
          this.riveAnimation?.resizeDrawingSurfaceToCanvas();
          this.loading = false;
          this.loadCompleted.emit();
        },
        onLoadError: () => {
          console.error(`Failed to load Rive animation from path: ${this.path}`);
          this.loading = false;
        },
        ...this.sbRiveAnimationConfig(),
      });
    }
  }

  play() {
    this.riveAnimation?.play();
  }

  reset(autoplay?: boolean) {
    this.loading = true;
    // (Re)loading the animation is more reliable than calling this.riveAnimation.reset()
    this.riveAnimation?.load({
      src: this.path,
      autoplay: autoplay ?? this.sbRiveAnimationConfig().autoplay,
    });
  }

  stop() {
    this.reset(false);
  }

  ngOnDestroy() {
    this.riveAnimation?.cleanup();
  }
}
